import React, { useState, useEffect, useRef } from 'react';
import { Lightbulb, Users, BookOpen, MessageSquare, Mail, Phone, MapPin, Linkedin, Twitter, Facebook, Star, Edit3, ShieldCheck, Brain, UserCog2, Handshake, Zap, ChevronLeft, ChevronRight, FileText, CheckSquare, Video, ArrowRight, User } from 'lucide-react';
import './tailwind.css';

// Company Information
const companyInfo = {
  name: "SPICES",
  fullName: "School Psychologists Improving Climate in Educational Settings",
  tagline: "Cultivating Positive and Inclusive School Environments",
  email: "Raquel@Spices.Support",
  phone: "267-221-9698",
  address: "Serving the DMV area", 
  logo: () => <Lightbulb className="h-10 w-10 text-slate-600" />, 
  footerText: `© ${new Date().getFullYear()} SPICES (School Psychologists Improving Climate in Educational Settings). All rights reserved.`,
  domain: "SPICES.Support"
};

// Employee Data
const employeesData = [
  {
    id: 1,
    name: "Raquel Sheetz, Psy.S., NCSP, LSSP",
    title: "Owner & CEO",
    bio: "Raquel brings nearly two decades of diverse educational experience, from preschool to college. A Nationally Certified School Psychologist (NCSP) and Licensed Specialist in School Psychology (LSSP), she has spent 15 years in school administration, championing social-emotional learning, MTSS, and inclusive programmatic changes. Raquel excels in creative budget management and has supervised a wide range of educational professionals.",
    hasCustomImage: true, 
    imageUrl: "Head Shot2.jpg", 
    linkedin: "#",
    twitter: "#",
    keyCertifications: [
      "Nationally Certified School Psychologist (NCSP)",
      "Licensed Specialist in School Psychology (LSSP)",
      "15+ Years in K-12 District Administration"
    ]
  },
  {
    id: 2,
    name: "Nicole Campanella, M.A., Ed.S.",
    title: "Co-Founder & Clinical Specialist",
    bio: "Nicole is a dedicated practitioner with extensive experience evaluating learning vulnerabilities, configuring tier-based behavior models, and guiding parents through complex multivariable data reviews. She specializes in designing customized strategic recommendations that align home strategy directly with K-12 academic accommodations.",
    hasCustomImage: false, 
    imageUrl: "nicole-headshot.jpg",
    linkedin: "#",
    twitter: "#",
    keyCertifications: [
      "Certified School Psychologist",
      "Special Education Consultation & Advocacy Design",
      "Multi-Tiered Systems of Support (MTSS) Implementation Specialist"
    ]
  }
];

// Testimonials Data updated with real feedback
const testimonialsData = [
  {
    id: 1,
    text: "We appreciated the Program Review Process! It brought all of us together—including GE teachers, principal, admin, program specialist, ed specialists, MHT, School psych, etc. We loved observing co-teaching within the classroom setting and then getting to debrief and self-assess our Special Ed program as a whole. It was nice to be able to rate the school and know what needs to happen in order for change to occur.",
    author: "Special Education Teammates",
    title: "Program Evaluation Group",
    company: "Aspire Public Schools",
    rating: 5
  }
];

// Services/Pillars Data
const spicesPillarData = [
  {
    id: 's',
    letter: "S",
    title: "School Psychologists",
    tagline: "Empowering practitioners, optimizing compliance.",
    desc: "We provide comprehensive professional development, mentoring frameworks, and workload optimization strategies for school psychology teams. From modernizing evaluation procedures to balancing complex diagnostic portfolios, we help practitioners align compliance requirements with deep student support.",
    services: [
      "Psychometric Test Interpretation Modules",
      "Evaluation Workflow & Backlog Optimization",
      "Practitioner Mentorship & Retention Programming",
      "Independent Educational Evaluation (IEE) Reviews"
    ],
    icon: <Brain className="h-6 w-6 text-slate-600" />
  },
  {
    id: 'p',
    letter: "P",
    title: "Program Integration",
    tagline: "Breaking down programmatic silos.",
    desc: "We build unified operational connections across general education, special education, and specialized student services. Our strategic design ensures that administrative systems, resource allotment rules, and localized teaching frameworks operate in lockstep.",
    services: [
      "Co-Teaching & Collaboration Blueprints",
      "Administrative Resource Allotment Reviews",
      "Unified Program Compliance Audits",
      "Cross-Department Communication Workflows"
    ],
    icon: <Handshake className="h-6 w-6 text-slate-600" />
  },
  {
    id: 'i',
    letter: "I",
    title: "Improving Climate",
    tagline: "Fostering psychological safety and belonging.",
    desc: "We help schools implement systemic behavior systems that noticeably drop exclusionary discipline rates while elevating staff morale. We focus on transforming school environments into centers of psychological wellness and mutual respect.",
    services: [
      "School-Wide Positive Behavior Support (SWPBIS)",
      "Trauma-Informed Classroom Integration",
      "Staff Burnout Audits & Resiliency Frameworks",
      "Restorative Circle Implementation Guides"
    ],
    icon: <ShieldCheck className="h-6 w-6 text-slate-600" />
  },
  {
    id: 'c',
    letter: "C",
    title: "Climate Assessment",
    tagline: "Data-driven culture transformations.",
    desc: "We deploy multi-variable cultural audits, perception inventories, and behavioral data maps to discover hidden institutional stressors. We convert scattered qualitative experiences into clear metrics that drive sustainable campus transformations.",
    services: [
      "Comprehensive Campus Audits",
      "Stakeholder Perception Inventories",
      "Behavioral Disproportionality Data Mapping",
      "Post-Audit Strategic Change Plans"
    ],
    icon: <Edit3 className="h-6 w-6 text-slate-600" />
  },
  {
    id: 'e',
    letter: "E",
    title: "Educational Systems",
    tagline: "Scaling district-wide systemic health.",
    desc: "We consult directly on complex, macro-level infrastructure projects including Multi-Tiered Systems of Support (MTSS) scaling, threat assessment parameters, and section 504 standardized execution models across expansive regional frameworks.",
    services: [
      "District-Wide MTSS Multi-Tier Structuring",
      "Behavioral Threat Assessment Optimization",
      "Section 504 Operational Policy Standardization",
      "State-Level Grant Alignment Frameworks"
    ],
    icon: <UserCog2 className="h-6 w-6 text-slate-600" />
  },
  {
    id: 's2',
    letter: "S",
    title: "Supportive Solutions",
    tagline: "Agile interventions for complex structural friction.",
    desc: "We deliver rapid-deployment behavioral coaching models, localized student crisis framework planning, and neutral mediation spaces for high-friction conflicts between institutional administrations and family networks.",
    services: [
      "Targeted Crisis Management Playbooks",
      "Administrative Behavioral Coaching Paths",
      "High-Friction IEP Mediation Support",
      "De-escalation Professional Training Paths"
    ],
    icon: <Zap className="h-6 w-6 text-slate-600" />
  }
];

function App() {
  const [activePage, setActivePage] = useState('home');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activePillar, setActivePillar] = useState('s');
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [parentFormSubmitted, setParentFormSubmitted] = useState(false);

  // Parent Consultation Form State
  const [parentFormData, setParentFormData] = useState({
    parentName: '',
    email: '',
    childGrade: '',
    schoolDistrict: '',
    supportType: 'advocacy',
    notes: ''
  });

  const scrollRef = useRef(null);
  const formRef = useRef(null);

  const navLinks = [
    { id: 'home', label: 'Home' },
    { id: 'about', label: 'About Us' },
    { id: 'services', label: 'School Services' },
    { id: 'parents', label: 'Parent Hub' }, 
    { id: 'testimonials', label: 'Testimonials' },
    { id: 'contact', label: 'Contact' }
  ];

  // Auto-rotation for testimonials
  useEffect(() => {
    if (activePage === 'home' || activePage === 'testimonials') {
      const timer = setInterval(() => {
        setCurrentTestimonial((prev) => (prev + 1) % testimonialsData.length);
      }, 8000);
      return () => clearInterval(timer);
    }
  }, [activePage]);

  const handleParentFormSubmit = (e) => {
    e.preventDefault();
    setParentFormSubmitted(true);
  };

  const selectedPillarData = spicesPillarData.find(p => p.id === activePillar) || spicesPillarData[0];

  return (
    <div className="min-h-screen flex flex-col justify-between bg-slate-50 text-slate-800 font-sans antialiased">
      {/* HEADER & NAVIGATION */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3 cursor-pointer" onClick={() => setActivePage('home')}>
              {companyInfo.logo()}
              <div>
                <span className="text-xl font-bold tracking-wider text-slate-800 block">SPICES</span>
                <span className="text-[10px] text-slate-500 uppercase tracking-widest hidden lg:block font-medium">School Psychologists Improving Climate in Educational Settings</span>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-8">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => { setActivePage(link.id); window.scrollTo(0,0); }}
                  className={`text-xs font-medium tracking-wide transition-all duration-200 pb-1 border-b-2 uppercase ${
                    activePage === link.id
                      ? 'border-slate-600 text-slate-900 font-semibold'
                      : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300'
                  }`}
                >
                  {link.label}
                </button>
              ))}
            </nav>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 text-slate-600 hover:text-slate-900 focus:outline-none rounded-lg hover:bg-slate-100 transition-colors"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L12 12M14 6l8 8" /></svg>
                ) : (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Dropdown */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-b border-slate-200 shadow-inner">
            <div className="px-2 pt-2 pb-4 space-y-1 sm:px-3">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => {
                    setActivePage(link.id);
                    setIsMobileMenuOpen(false);
                    window.scrollTo(0,0);
                  }}
                  className={`block w-full text-left px-4 py-3 rounded-xl text-base font-medium transition-all ${
                    activePage === link.id
                      ? 'bg-slate-100 text-slate-900 font-semibold pl-6 border-l-4 border-slate-600'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  {link.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* RENDER PAGES DYNAMICALLY */}
      <main className="flex-grow">
        
        {/* HOMEPAGE VIEW */}
        {activePage === 'home' && (
          <div>
            {/* Hero Section */}
            <section className="relative bg-white border-b border-slate-200 py-20 sm:py-28 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-slate-50 opacity-70 z-0"></div>
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
                <h1 className="text-4xl sm:text-6xl font-extrabold text-slate-900 tracking-tight leading-tight max-w-5xl mx-auto">
                  {companyInfo.fullName} (<span className="text-slate-600 font-light italic">SPICES</span>)
                </h1>
                <p className="mt-6 text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto font-normal leading-relaxed">
                  {companyInfo.tagline}. We engineer strategic, data-centered operational plans tailored for districts, schools, and families.
                </p>
                <div className="mt-10 flex flex-wrap justify-center gap-4">
                  <button
                    onClick={() => { setActivePage('services'); window.scrollTo(0,0); }}
                    className="px-8 py-3.5 bg-slate-800 text-white rounded-xl font-medium shadow-md shadow-slate-900/10 hover:bg-slate-900 hover:shadow-lg transition-all text-sm uppercase tracking-wider"
                  >
                    School Solutions
                  </button>
                  <button
                    onClick={() => { setActivePage('parents'); window.scrollTo(0,0); }}
                    className="px-8 py-3.5 bg-white text-slate-700 border border-slate-300 rounded-xl font-medium hover:bg-slate-50 transition-all text-sm uppercase tracking-wider flex items-center gap-2"
                  >
                    Parent Section <ArrowRight size={15} />
                  </button>
                </div>
              </div>
            </section>

            {/* Core Capabilities Intro */}
            <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center max-w-3xl mx-auto mb-16">
                <span className="text-xs font-bold tracking-widest text-slate-500 uppercase">Our Capabilities</span>
                <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl mt-2">Comprehensive Systemic Impact</h2>
                <p className="mt-4 text-sm sm:text-base text-slate-600">We look at school ecosystems holistically, merging administrative policy requirements with student learning and home routines.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div 
                  onClick={() => { setActivePage('services'); setActivePillar('s'); window.scrollTo(0,0); }}
                  className="bg-white border border-slate-200 rounded-2xl p-8 hover:shadow-xl hover:border-slate-300 transition-all cursor-pointer group"
                >
                  <div className="p-3 bg-slate-100 rounded-xl inline-block group-hover:bg-slate-800 group-hover:text-white transition-colors mb-5">
                    <Brain size={24} />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">School Psychology Systems</h3>
                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">Direct assistance with diagnostic tracking, psychometric reviews, and practitioner mentorship tracks.</p>
                </div>

                <div 
                  onClick={() => { setActivePage('services'); setActivePillar('i'); window.scrollTo(0,0); }}
                  className="bg-white border border-slate-200 rounded-2xl p-8 hover:shadow-xl hover:border-slate-300 transition-all cursor-pointer group"
                >
                  <div className="p-3 bg-slate-100 rounded-xl inline-block group-hover:bg-slate-800 group-hover:text-white transition-colors mb-5">
                    <ShieldCheck size={24} />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Climate &amp; Behavior Architecture</h3>
                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">Building system-wide SWPBIS plans and trauma-informed setups to safely drop exclusionary discipline patterns.</p>
                </div>

                <div 
                  onClick={() => { setActivePage('parents'); window.scrollTo(0,0); }}
                  className="bg-white border border-slate-200 rounded-2xl p-8 hover:shadow-xl hover:border-slate-300 transition-all cursor-pointer group border-dashed border-slate-300 bg-gradient-to-b from-white to-slate-50/50"
                >
                  <div className="p-3 bg-slate-100 rounded-xl inline-block group-hover:bg-slate-800 group-hover:text-white transition-colors mb-5">
                    <Users size={24} />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Parent Hub &amp; Advocacy</h3>
                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">Complete special education advocacy, step-by-step IEP/504 path blueprints, and proactive behavioral training paths.</p>
                </div>
              </div>
            </section>

            {/* Testimonial Block */}
            <section className="bg-slate-900 text-white py-20 border-t border-slate-800 overflow-hidden">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
                <span className="text-xs font-bold tracking-widest text-slate-400 uppercase">Trusted Endorsements</span>
                <div className="mt-8 relative min-h-[180px] flex items-center justify-center">
                  <p className="text-sm sm:text-base italic text-slate-300 font-light leading-relaxed max-w-4xl mx-auto px-4">
                    "{testimonialsData[currentTestimonial].text}"
                  </p>
                </div>
                <div className="mt-6">
                  <h4 className="text-sm font-bold tracking-wide text-white">{testimonialsData[currentTestimonial].author}</h4>
                  <p className="text-xs text-slate-400 mt-1">{testimonialsData[currentTestimonial].title}, {testimonialsData[currentTestimonial].company}</p>
                </div>
                <div className="flex justify-center space-x-2 mt-8">
                  {testimonialsData.map((_, i) => (
                    <button 
                      key={i} 
                      onClick={() => setCurrentTestimonial(i)}
                      className={`h-1.5 rounded-full transition-all duration-300 ${currentTestimonial === i ? 'w-6 bg-white' : 'w-2 bg-slate-700'}`}
                      aria-label={`Go to testimonial ${i + 1}`}
                    />
                  ))}
                </div>
              </div>
            </section>
          </div>
        )}

        {/* ABOUT US PAGE */}
        {activePage === 'about' && (
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 space-y-16">
            <div className="text-center max-w-2xl mx-auto space-y-3">
              <span className="text-xs font-bold tracking-widest text-slate-500 uppercase">Meet the Leadership</span>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Dedicated to Systemic Impact</h1>
              <p className="text-slate-600 text-sm sm:text-base leading-relaxed">Bringing combined administrative oversight with deep clinical psychometric expertise.</p>
            </div>

            <div className="space-y-16">
              {employeesData.map((employee) => (
                <div key={employee.id} className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-10 shadow-sm grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
                  
                  {/* Photo Framework Column */}
                  <div className="flex flex-col items-center justify-center">
                    {employee.hasCustomImage ? (
                      <img 
                        src={employee.imageUrl} 
                        alt={employee.name} 
                        className="w-48 h-48 sm:w-56 sm:h-56 rounded-full object-cover border-4 border-slate-100 shadow-md"
                      />
                    ) : (
                      <div className="w-48 h-48 sm:w-56 sm:h-56 rounded-full bg-slate-100 border-2 border-dashed border-slate-300 shadow-inner flex flex-col items-center justify-center p-4 text-center">
                        <User className="h-12 w-12 text-slate-400" />
                      </div>
                    )}
                  </div>

                  {/* Bio Context Column */}
                  <div className="md:col-span-2 space-y-4">
                    <div>
                      <h2 className="text-2xl font-bold text-slate-900">{employee.name}</h2>
                      <p className="text-slate-500 text-xs sm:text-sm font-semibold tracking-wide uppercase mt-1">{employee.title}</p>
                    </div>
                    <p className="text-slate-600 text-xs sm:text-sm leading-relaxed whitespace-pre-line">{employee.bio}</p>
                    
                    <div className="pt-4 border-t border-slate-200">
                      <h3 className="font-bold text-slate-800 text-xs uppercase tracking-wider mb-3 flex items-center gap-1.5">
                        <ShieldCheck size={16} className="text-slate-600" /> Professional Designations
                      </h3>
                      <ul className="space-y-2">
                        {employee.keyCertifications.map((cert, idx) => (
                          <li key={idx} className="text-slate-600 text-xs flex items-center gap-2">
                            <span className="w-1.5 h-1.5 bg-slate-400 rounded-full"></span> {cert}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* SCHOOL SERVICES PAGE */}
        {activePage === 'services' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Sidebar selection */}
            <div className="space-y-4">
              <div>
                <span className="text-xs font-bold tracking-widest text-slate-500 uppercase">Our Capabilities</span>
                <h1 className="text-3xl font-bold text-slate-900 tracking-tight mt-1">District Solutions</h1>
              </div>
              <p className="text-slate-600 text-xs sm:text-sm leading-relaxed mb-6">
                Select an operational focus track below to inspect specific clinical compliance strategies and deployment modules.
              </p>
              <div className="flex flex-col gap-2" ref={scrollRef}>
                {spicesPillarData.map((pillar) => (
                  <button
                    key={pillar.id}
                    onClick={() => setActivePillar(pillar.id)}
                    className={`p-4 rounded-xl text-left border flex items-center justify-between transition-all duration-200 ${
                      activePillar === pillar.id
                        ? 'bg-slate-800 text-white border-transparent shadow-md'
                        : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`text-xl font-black tracking-wider ${activePillar === pillar.id ? 'text-slate-300' : 'text-slate-400'}`}>{pillar.letter}</span>
                      <span className="font-semibold text-xs sm:text-sm">{pillar.title}</span>
                    </div>
                    <ChevronRight size={16} />
                  </button>
                ))}
              </div>
            </div>

            {/* Detail Card View */}
            <div className="lg:col-span-2 bg-white p-6 sm:p-10 rounded-2xl border border-slate-200 shadow-sm space-y-6 flex flex-col justify-between">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-slate-100 rounded-xl text-slate-700">
                    {selectedPillarData.icon}
                  </div>
                  <div>
                    <span className="text-[10px] font-bold tracking-widest text-slate-400 uppercase">Active Core Component</span>
                    <h2 className="text-2xl font-bold text-slate-900 mt-0.5">{selectedPillarData.title}</h2>
                  </div>
                </div>
                <p className="text-sm italic font-medium text-slate-500">{selectedPillarData.tagline}</p>
                <p className="text-slate-600 text-xs sm:text-sm leading-relaxed pt-2">{selectedPillarData.desc}</p>
              </div>

              <div className="border-t border-slate-100 pt-6 mt-6">
                <h3 className="text-slate-800 font-bold text-xs uppercase tracking-wider mb-4">Target Functional Sub-Offerings:</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {selectedPillarData.services.map((item, idx) => (
                    <div key={idx} className="p-3 bg-slate-50 rounded-xl text-slate-700 text-xs border border-slate-200/60 font-medium flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-slate-400 rounded-full shrink-0"></span>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PARENT HUB PAGE */}
        {activePage === 'parents' && (
          <div className="bg-slate-50 min-h-screen">
            {/* Context Header Banner */}
            <section className="bg-white border-b border-slate-200 py-16 px-4">
              <div className="max-w-4xl mx-auto text-center space-y-4">
                <span className="text-xs font-bold tracking-widest text-slate-500 uppercase">Advocacy &amp; Training</span>
                <h1 className="text-3xl sm:text-5xl font-extrabold text-slate-900 tracking-tight">
                  Parent Hub
                </h1>
                <p className="text-sm sm:text-base text-slate-600 max-w-2xl mx-auto leading-relaxed">
                  Navigating the special education ecosystem shouldn't feel like an isolating challenge. We work side-by-side with families to unlock clear data paths, understand rights, and build supportive behavior strategies at home.
                </p>
              </div>
            </section>

            {/* Premium Concierge Report Analysis */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12">
              <div className="bg-slate-900 text-white rounded-2xl p-6 sm:p-10 border border-slate-800 shadow-md md:flex items-center justify-between gap-8">
                <div className="space-y-3 max-w-3xl">
                  <span className="text-[10px] font-bold tracking-widest text-slate-400 uppercase">Premium Concierge Service</span>
                  <h2 className="text-2xl font-bold tracking-tight">Comprehensive Educational Report Analysis</h2>
                  <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                    Hand over the dense 30+ page clinical neuropsychological or school evaluation data stacks. We completely review the documents, translate confusing psychometric standard scores into plain language, and map out your precise classroom accommodation strategies so you don't have to decode the jargon alone.
                  </p>
                </div>
                <div className="mt-6 md:mt-0 shrink-0">
                  <button 
                    onClick={() => formRef.current?.scrollIntoView({ behavior: 'smooth' })}
                    className="w-full md:w-auto px-6 py-3 bg-white text-slate-900 text-xs font-bold uppercase tracking-wider rounded-xl hover:bg-slate-100 transition-all shadow-md shrink-0 whitespace-nowrap"
                  >
                    Inquire Now
                  </button>
                </div>
              </div>
            </section>

            {/* Split Feature Grid */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 lg:grid-cols-2 gap-12">
              
              {/* Column 1: Advocacy */}
              <div className="space-y-6 bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-sm">
                <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
                  <div className="p-3 bg-slate-100 text-slate-700 rounded-xl">
                    <ShieldCheck size={24} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">Special Education Advocacy</h2>
                    <p className="text-xs text-slate-500">Securing the structure your child legally deserves.</p>
                  </div>
                </div>

                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                  We empower families during critical multidisciplinary team meetings, helping bridge communication lines while keeping the central focus purely on measurable student outcomes.
                </p>

                <div className="space-y-4 pt-2">
                  <div className="flex gap-3.5 items-start">
                    <div className="mt-0.5 text-slate-500 shrink-0"><FileText size={18} /></div>
                    <div>
                      <h3 className="font-semibold text-slate-900 text-sm">IEP &amp; 504 Navigation Blueprints</h3>
                      <p className="text-xs text-slate-600 mt-0.5">Comprehensive, structured path guidance through initial referrals, multi-variable evaluations, eligibility matrices, and annual goals modeling.</p>
                    </div>
                  </div>

                  <div className="flex gap-3.5 items-start border-t border-slate-50 pt-4">
                    <div className="mt-0.5 text-slate-500 shrink-0"><CheckSquare size={18} /></div>
                    <div>
                      <h3 className="font-semibold text-slate-900 text-sm">School Meeting Alignment</h3>
                      <p className="text-xs text-slate-600 mt-0.5">Strategic preparation blueprints for eligibility sessions, annual plan re-evaluations, and manifestation determination reviews.</p>
                    </div>
                  </div>

                  <div className="flex gap-3.5 items-start border-t border-slate-50 pt-4">
                    <div className="mt-0.5 text-slate-500 shrink-0"><MessageSquare size={18} /></div>
                    <div>
                      <h3 className="font-semibold text-slate-900 text-sm">Collaborative Accountability Care</h3>
                      <p className="text-xs text-slate-600 mt-0.5">Coaching techniques constructed to ensure school teams meet student resource requirements while maintaining healthy relationships.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Column 2: Training */}
              <div className="space-y-6 bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-sm">
                <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
                  <div className="p-3 bg-slate-100 text-slate-700 rounded-xl">
                    <BookOpen size={24} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">Evidence-Based Parent Training</h2>
                    <p className="text-xs text-slate-500">Bringing professional methodology directly home.</p>
                  </div>
                </div>

                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                  We translate complex clinical interventions into straightforward, actionable frameworks parents can deploy safely within everyday domestic patterns.
                </p>

                <div className="space-y-4 pt-2">
                  <div className="flex gap-3.5 items-start">
                    <div className="mt-0.5 text-slate-500 shrink-0"><Video size={18} /></div>
                    <div>
                      <h3 className="font-semibold text-slate-900 text-sm">Positive Behavior Support Modeling</h3>
                      <p className="text-xs text-slate-600 mt-0.5">Practical structures for modern families to reduce household behavior friction using routine adjustments, antecedent engineering, and positive tracking workflows.</p>
                    </div>
                  </div>

                  <div className="flex gap-3.5 items-start border-t border-slate-50 pt-4">
                    <div className="mt-0.5 text-slate-500 shrink-0"><Lightbulb size={18} /></div>
                    <div>
                      <h3 className="font-semibold text-slate-900 text-sm">Executive Function Mastery</h3>
                      <p className="text-xs text-slate-600 mt-0.5">Strategies to improve student independence, spatial organization, time visualization, task initiation sequences, and emotional modulation.</p>
                    </div>
                  </div>

                  <div className="flex gap-3.5 items-start border-t border-slate-50 pt-4">
                    <div className="mt-0.5 text-slate-500 shrink-0"><Brain size={18} /></div>
                    <div>
                      <h3 className="font-semibold text-slate-900 text-sm">Demystifying Complex School Data</h3>
                      <p className="text-xs text-slate-600 mt-0.5">A clear translation guide showing parents exactly how to read psychometric bell curve standard deviations, standard scores, and progress metrics.</p>
                    </div>
                  </div>
                </div>
              </div>

            </div>

            {/* Consultation Form Section */}
            <section ref={formRef} className="bg-white border-t border-slate-200 py-16 px-4">
              <div className="max-w-3xl mx-auto bg-slate-50 rounded-2xl p-6 sm:p-10 border border-slate-200 shadow-inner">
                <div className="text-center mb-8 space-y-1">
                  <h2 className="text-2xl font-bold text-slate-900">Request an Advocacy Consultation</h2>
                  <p className="text-slate-500 text-xs sm:text-sm">Connect directly with Raquel to map out targeted support or report interpretation modules.</p>
                </div>

                {parentFormSubmitted ? (
                  <div className="bg-slate-900 border border-slate-800 text-white p-8 rounded-xl text-center space-y-2">
                    <h3 className="font-bold text-base tracking-wide uppercase">Inquiry Successfully Documented</h3>
                    <p className="text-xs text-slate-400 max-w-md mx-auto">Thank you for sharing your context. We will review your child's parameters and connect back within 24 to 48 business hours.</p>
                  </div>
                ) : (
                  <form onSubmit={handleParentFormSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Parent/Guardian Name</label>
                        <input 
                          type="text" required
                          className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-slate-400 focus:border-transparent bg-white text-xs sm:text-sm"
                          value={parentFormData.parentName}
                          onChange={(e) => setParentFormData({...parentFormData, parentName: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Email Address</label>
                        <input 
                          type="email" required
                          className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-slate-400 focus:border-transparent bg-white text-xs sm:text-sm"
                          value={parentFormData.email}
                          onChange={(e) => setParentFormData({...parentFormData, email: e.target.value})}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Child's Grade Level</label>
                        <input 
                          type="text" required placeholder="e.g. 3rd Grade, Middle School"
                          className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-slate-400 focus:border-transparent bg-white text-xs sm:text-sm"
                          value={parentFormData.childGrade}
                          onChange={(e) => setParentFormData({...parentFormData, childGrade: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">School District / County</label>
                        <input 
                          type="text" required placeholder="e.g. Local County School District"
                          className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-slate-400 focus:border-transparent bg-white text-xs sm:text-sm"
                          value={parentFormData.schoolDistrict}
                          onChange={(e) => setParentFormData({...parentFormData, schoolDistrict: e.target.value})}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Primary Support Framework Needed</label>
                      <select 
                        className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-slate-400 focus:border-transparent bg-white text-xs sm:text-sm"
                        value={parentFormData.supportType}
                        onChange={(e) => setParentFormData({...parentFormData, supportType: e.target.value})}
                      >
                        <option value="advocacy">Special Education Advocacy &amp; IEP Alignment</option>
                        <option value="report">Comprehensive Report Analysis (Paperwork Review)</option>
                        <option value="training">Actionable Home Parent Training Modules</option>
                        <option value="both">Comprehensive Support Path (All Services)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Briefly Outline Your Current Learning or Behavioral Concerns</label>
                      <textarea 
                        rows="4" required placeholder="Please outline if your child is currently processing an active IEP evaluation track, transitioning via a 504 plan framework, or facing daily executive functioning bottlenecks..."
                        className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-slate-400 focus:border-transparent bg-white text-xs sm:text-sm"
                        value={parentFormData.notes}
                        onChange={(e) => setParentFormData({...parentFormData, notes: e.target.value})}
                      />
                    </div>

                    <button 
                      type="submit"
                      className="w-full bg-slate-800 hover:bg-slate-900 text-white font-semibold py-3 px-6 rounded-xl transition-all shadow-sm flex items-center justify-center gap-2 text-xs sm:text-sm uppercase tracking-wider"
                    >
                      Submit Consultation Request <ArrowRight size={14} />
                    </button>
                  </form>
                )}
              </div>
            </section>
          </div>
        )}

        {/* TESTIMONIALS STANDALONE PAGE */}
        {activePage === 'testimonials' && (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 space-y-12">
            <div className="text-center max-w-xl mx-auto space-y-2">
              <span className="text-xs font-bold tracking-widest text-slate-500 uppercase">Success Metrics</span>
              <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Validated System Outcomes</h1>
              <p className="text-slate-600 text-xs sm:text-sm">Real structural improvements noted by specialized K-12 administrators and directors.</p>
            </div>

            <div className="space-y-6">
              {testimonialsData.map((item) => (
                <div key={item.id} className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-sm space-y-4">
                  <div className="flex text-amber-500 gap-0.5">
                    {[...Array(item.rating)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
                  </div>
                  <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">"{item.text}"</p>
                  <div className="pt-2 border-t border-slate-100 flex justify-between items-center">
                    <div>
                      <h3 className="text-sm font-bold text-slate-900">{item.author}</h3>
                      <p className="text-[11px] text-slate-500">{item.title}, {item.company}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CONTACT PAGE WITH GOOGLE BOOKING ENGINE */}
        {activePage === 'contact' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
            
            {/* Direct Contact Parameter Grid */}
            <div className="space-y-6 lg:col-span-1">
              <div>
                <span className="text-xs font-bold tracking-widest text-slate-500 uppercase">Inquiries</span>
                <h1 className="text-3xl font-bold text-slate-900 tracking-tight mt-1">Get in Touch</h1>
                <p className="text-slate-600 text-xs sm:text-sm mt-3 leading-relaxed">
                  Let us assist your school district configuration or organize specialized parent consultation sessions. Select a time slice immediately inside our embedded calendar module.
                </p>
              </div>

              <div className="space-y-4 pt-4">
                <div className="flex items-center gap-3 text-slate-600">
                  <div className="p-3 bg-slate-100 rounded-xl text-slate-700"><Mail size={18} /></div>
                  <span className="text-xs sm:text-sm font-medium">{companyInfo.email}</span>
                </div>
                <div className="flex items-center gap-3 text-slate-600">
                  <div className="p-3 bg-slate-100 rounded-xl text-slate-700"><Phone size={18} /></div>
                  <span className="text-xs sm:text-sm font-medium">{companyInfo.phone}</span>
                </div>
                <div className="flex items-center gap-3 text-slate-600">
                  <div className="p-3 bg-slate-100 rounded-xl text-slate-700"><MapPin size={18} /></div>
                  <span className="text-xs sm:text-sm font-medium">{companyInfo.address}</span>
                </div>
              </div>
            </div>

            {/* Embedded Google Calendar Appointment Scheduling */}
            <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-4 sm:p-6 shadow-sm overflow-hidden">
              <iframe 
                src="https://calendar.google.com/calendar/appointments/schedules/AcZssZ2KgFiAYiozFmIx6swPTDYREd4xAcrkYL0iA6OcA3wWAk5coZsKJ5J4PipwSKXGj8r-sJsdZrey?gv=true" 
                style={{ border: 0 }} 
                width="100%" 
                height="600" 
                frameBorder="0"
                title="Google Calendar Appointment Scheduling"
              ></iframe>
            </div>

          </div>
        )}

      </main>

      {/* GLOBAL FOOTER ELEMENT */}
      <footer className="bg-slate-900 text-slate-400 py-12 border-t border-slate-800 shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <h3 className="text-white text-lg font-bold mb-3 tracking-wide">SPICES</h3>
            <p className="text-[11px] leading-relaxed max-w-sm">School Psychologists Improving Climate in Educational Settings. Cultivating inclusive, data-driven excellence across all educational pathways.</p>
          </div>
          <div>
            <h3 className="text-white text-xs font-semibold mb-3 tracking-wider uppercase">Site Navigation</h3>
            <ul className="space-y-2">
              {navLinks.map((link) => (
                <li key={`footer-${link.id}`}>
                  <button 
                    onClick={() => { setActivePage(link.id); window.scrollTo(0,0); }} 
                    className="text-xs hover:text-slate-100 transition-colors uppercase tracking-wider text-[10px]"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-white text-xs font-semibold mb-3 tracking-wider uppercase">Direct Connect</h3>
            <p className="text-xs mb-1">Email: <a href={`mailto:${companyInfo.email}`} className="hover:text-slate-100 font-medium">{companyInfo.email}</a></p>
            <p className="text-xs mb-3">Phone: <a href={`tel:${companyInfo.phone.replace(/-/g, "")}`} className="hover:text-slate-100 font-medium">{companyInfo.phone}</a></p>
            <div className="flex space-x-3 text-slate-400">
              <a href="#" aria-label="SPICES Facebook" className="hover:text-slate-100 transition-colors"><Facebook size={18}/></a>
              <a href="#" aria-label="SPICES Twitter" className="hover:text-slate-100 transition-colors"><Twitter size={18}/></a>
              <a href="#" aria-label="SPICES LinkedIn" className="hover:text-slate-100 transition-colors"><Linkedin size={18}/></a>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 pt-8 border-t border-slate-800 text-center text-xs text-slate-500">
          <p>{companyInfo.footerText}</p>
          <p className="mt-1 font-light italic">Designed with AI Assistance</p>
        </div>
      </footer>
    </div>
  );
}

export default App;